import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/**
 * Comptes du personnel et des formateurs (écran `dir-utilisateurs`, TF-500,
 * direction seule) : la liste, l'invitation d'un nouvel utilisateur, son
 * renvoi, la désactivation et la réactivation. L'API rend l'état de chaque
 * compte et ce qui empêche chaque geste ; l'écran n'en recalcule rien.
 */

export type EtatCompte = 'ACTIF' | 'JAMAIS_CONNECTE' | 'INVITATION_EN_ATTENTE' | 'INVITATION_EXPIREE' | 'DESACTIVE';

export interface LigneCompte {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  profil: string;
  libelleProfil: string;
  sites: string[];
  etat: EtatCompte;
  libelleEtat: string;
  dernierAcces: string | null;
  creeLe: string;
  invitation: { envoyeLe: string; expireLe: string; envois: number } | null;
  moi: boolean;
  refusRenvoi: string | null;
  refusDesactivation: string | null;
  refusReactivation: string | null;
}

export interface VueUtilisateurs {
  antennes: { code: string; nom: string }[];
  profils: { profil: string; libelle: string; sousProfils: { sousProfil: string; libelle: string }[] }[];
  statutsFormateur: { statut: string; libelle: string }[];
  etats: { etat: EtatCompte; libelle: string }[];
  validiteInvitationJours: number;
  courrielExpedie: boolean;
  compteurs: Record<EtatCompte, number> & { total: number };
  utilisateurs: LigneCompte[];
}

export interface FiltresUtilisateurs {
  site: string;
  recherche: string;
  profil: string;
  etat: string;
}

export interface DemandeInvitation {
  email: string;
  nom: string;
  prenom: string;
  profil: string;
  sousProfil: string | null;
  sites: string[];
  statutFormateur: string | null;
}

/** Le lien n'est rendu que s'il n'est pas parti par e-mail : la direction le transmet. */
export interface ResultatInvitation {
  utilisateur: LigneCompte;
  envoi: { expedie: boolean; lien: string | null };
}

const CLE = ['utilisateurs'] as const;

function parametres(filtres: FiltresUtilisateurs): string {
  const valeurs = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres)) {
    if (!valeur || (cle === 'site' && valeur === 'TOUS')) continue;
    valeurs.set(cle, valeur);
  }
  return valeurs.toString();
}

export function useUtilisateurs(filtres: FiltresUtilisateurs) {
  const chaine = parametres(filtres);
  return useQuery({
    queryKey: [...CLE, chaine],
    queryFn: () => appelApi<VueUtilisateurs>(`/utilisateurs${chaine ? `?${chaine}` : ''}`),
    placeholderData: keepPreviousData,
  });
}

/** Chaque geste relit la liste : compteurs et filtres en dépendent. */
function useGeste<E, R>(geste: (entree: E) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: geste,
    onSuccess: () => client.invalidateQueries({ queryKey: CLE }),
  });
}

export function useInviterUtilisateur() {
  return useGeste((demande: DemandeInvitation) =>
    appelApi<ResultatInvitation>('/utilisateurs/invitations', { methode: 'POST', corps: demande }),
  );
}

export function useRenvoyerInvitation() {
  return useGeste((id: string) =>
    appelApi<ResultatInvitation>(`/utilisateurs/${encodeURIComponent(id)}/invitation`, { methode: 'POST' }),
  );
}

export function useDesactiverCompte() {
  return useGeste((id: string) =>
    appelApi<LigneCompte>(`/utilisateurs/${encodeURIComponent(id)}/desactivation`, { methode: 'POST' }),
  );
}

export function useReactiverCompte() {
  return useGeste((id: string) =>
    appelApi<LigneCompte>(`/utilisateurs/${encodeURIComponent(id)}/reactivation`, { methode: 'POST' }),
  );
}
